import {
    TimelineFilterValue,
    TimelinePresetOption,
} from '@/components/common/Filter/types'

import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'

dayjs.extend(utc)

const TIMELINE_DATE_FORMAT = 'YYYY-MM-DD'

export const formatLocalDateForTimeline = (date: Date) => {
    return dayjs(date).format(TIMELINE_DATE_FORMAT)
}

export const parseTimelineDateToLocal = (formattedDate?: string) => {
    if (!formattedDate) return undefined

    const parsedDate = dayjs(formattedDate, TIMELINE_DATE_FORMAT)
    return parsedDate.isValid() ? parsedDate.local().toDate() : undefined
}

export const getLast12MonthsTimeline = (date: Date) => {
    const currentDate = dayjs.utc(date)

    const previousMonthStart = currentDate.subtract(1, 'year').add(1, 'day')

    return {
        startDate: previousMonthStart.format(TIMELINE_DATE_FORMAT),
        endDate: currentDate.format(TIMELINE_DATE_FORMAT),
    }
}

export const getPreviousMonthTimeline = (date: Date) => {
    const currentDate = dayjs.utc(date)

    const previousMonthStart = currentDate.subtract(1, 'month').startOf('month')
    const previousMonthEnd = currentDate.subtract(1, 'month').endOf('month')

    return {
        startDate: previousMonthStart.format(TIMELINE_DATE_FORMAT),
        endDate: previousMonthEnd.format(TIMELINE_DATE_FORMAT),
    }
}

export const getPrevious2MonthsTimeline = (date: Date) => {
    const currentDate = dayjs.utc(date)

    const previous2MonthsStart = currentDate
        .subtract(2, 'month')
        .startOf('month')
    const previous2MonthsEnd = currentDate.subtract(1, 'month').endOf('month')

    return {
        startDate: previous2MonthsStart.format(TIMELINE_DATE_FORMAT),
        endDate: previous2MonthsEnd.format(TIMELINE_DATE_FORMAT),
    }
}

export const getPreviousQuarterTimeline = (date: Date) => {
    const currentDate = dayjs.utc(date)

    const previousQuarterStart = currentDate
        .subtract(3, 'month')
        .startOf('month')
    const previousQuarterEnd = currentDate.subtract(1, 'month').endOf('month')

    return {
        startDate: previousQuarterStart.format(TIMELINE_DATE_FORMAT),
        endDate: previousQuarterEnd.format(TIMELINE_DATE_FORMAT),
    }
}

export const formatTimeline = (timeline: Partial<Timeline>) => {
    const start = dayjs(timeline.startDate).format("D MMM 'YY")
    const end = dayjs(timeline.endDate).format("D MMM 'YY")
    return `${start} - ${end}`
}

export const formatComparedToTimeline = (timeline: Timeline) => {
    const start = dayjs(timeline.startDate).format("MMM 'YY")
    const end = dayjs(timeline.endDate).format("MMM 'YY")
    return `${start} - ${end}`
}

export type Timeline = {
    startDate: string
    endDate: string
}

export const isTimeline = (value: unknown): value is Timeline => {
    return (
        typeof value === 'object' &&
        value !== null &&
        'startDate' in value &&
        !!value.startDate &&
        'endDate' in value &&
        !!value.endDate
    )
}

export const isTimelinePresetOption = (
    value: unknown
): value is TimelinePresetOption => {
    return (
        typeof value === 'string' &&
        [
            'last12Months',
            'previousMonth',
            'previous2Months',
            'previousQuarter',
        ].includes(value)
    )
}

export const isTimelineFilterValue = (
    timelineFilterValue: unknown
): timelineFilterValue is TimelineFilterValue => {
    if (
        typeof timelineFilterValue !== 'object' ||
        timelineFilterValue === null ||
        !('type' in timelineFilterValue) ||
        !('value' in timelineFilterValue)
    ) {
        return false
    }

    if (timelineFilterValue.type === 'is') {
        return (
            typeof timelineFilterValue.value === 'string' &&
            isTimelinePresetOption(timelineFilterValue.value)
        )
    }

    if (
        typeof timelineFilterValue.value !== 'object' ||
        timelineFilterValue.value === null
    ) {
        return false
    }

    return (
        'startDate' in timelineFilterValue.value &&
        !!timelineFilterValue.value.startDate &&
        'endDate' in timelineFilterValue.value &&
        !!timelineFilterValue.value.endDate
    )
}

export const areTimelinesEqual = (timeline1: Timeline, timeline2: Timeline) => {
    return (
        timeline1.startDate === timeline2.startDate &&
        timeline1.endDate === timeline2.endDate
    )
}

export const areTimelineFilterValuesEqual = (
    timeline1: TimelineFilterValue,
    timeline2: TimelineFilterValue
) => {
    if (timeline1.type === 'is' || timeline2.type === 'is') {
        return (
            timeline1.type === timeline2.type &&
            timeline1.value === timeline2.value
        )
    }

    return (
        timeline1.type === timeline2.type &&
        timeline1.value.startDate === timeline2.value.startDate &&
        timeline1.value.endDate === timeline2.value.endDate
    )
}

const FILTER_BUTTON_DATE_FORMAT = 'MM/DD/YYYY'

export const formatFilterButtonDate = (date: string) => {
    return dayjs(date).format(FILTER_BUTTON_DATE_FORMAT)
}
